import EChartCommon, {
  getAxisPointer,
} from '@/pages/opinion/components/EChartCommon';
import React, { useMemo } from 'react';
import usePcConsoleTimelineChart from '../../hooks/usePcConsoleTimelineChart';
import {
  formatTimeXAxisText,
  getChartPosition,
  setIntervalValue,
  setMinIntervalValue,
} from '@/pages/opinion/utils/chart';
import { echartsContent } from '@/pages/opinion/utils';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@/pages/opinion/components/ChartCommon/formatter';
import { useIntl } from 'umi';
import { NO_DATE } from '@/pages/opinion/constants';
import { percentageFormat, reviewsFormat } from '@/pages/opinion/utils/value';
const seriesDataKeys = [
  {
    text: 'Top Critic Average',
    key: 'top_critic_average',
    type: 'line',
    color: '#5086F3',
    valueKey: 'score',
  },
  {
    text: 'Critics Recommend%',
    key: 'critics_recommend',
    type: 'line',
    color: '#69D19C',
    valueKey: 'score',
  },
];
export default function OpenCriticScoreTrendBox({
  filterParams,
  platformData,
}: any) {
  const { chartMap, chartNoDataMap } = usePcConsoleTimelineChart({
    platformData,
    filterParams,
  });
  const { formatMessage } = useIntl();

  const options = useMemo(() => {
    const legends: { name: string; itemStyle: { color: string } }[] = [];
    const series: { name: string; type: string; data: any[] }[] = [];
    let xAxis: string[] = [];
    seriesDataKeys.map(({ text, key, type, color, valueKey }) => {
      const data = chartMap[key];
      const notPercentage = key === 'top_critic_average';
      if (data) {
        const seriesObj: any = {
          name: text,
          type,
          data: data.map((item) => {
            const value = item?.[valueKey];
            if (!value) return null;
            return notPercentage ? value : value / 100;
          }),
          itemStyle: {
            color,
          },
          key,
          yAxisIndex: notPercentage ? 0 : 1,
        };

        series.push(seriesObj);
        legends.push({
          name: text,
          itemStyle: { color },
        });

        if (xAxis.length === 0 && data) {
          xAxis = data.map((item) => item.time);
        }
      }
    });
    let currentOptions = {
      tooltip: {
        position: (
          pos: [any, any],
          params: any,
          dom: any,
          rect: any,
          size: { viewSize: [any, any] },
        ) =>
          getChartPosition(pos, params, dom, rect, size, 50, 15, 'bottom', 3),
        appendToBody: false,
        confine: true,
        formatter: (params: any) => {
          let title = listTimeTitleFunc(
            params?.[0]?.axisValue,
            filterParams.date_type,
            true,
          );
          return formatChartParamsDate({
            params,
            title: title,
            listTitleFunc: (params) => {
              return formatMessage({ id: params.seriesName || NO_DATE.EMPTY });
            },
            valueFormat: (value, ele) => {
              if (ele.seriesName === 'Top Critic Average') {
                return reviewsFormat(value);
              }
              return percentageFormat(value * 100, '-', 0);
            },
          });
        },
      },
      axisPointer: getAxisPointer((data) =>
        formatTimeXAxisText(data.value, filterParams?.date_type),
      ),
      grid: {
        top: 10,
        right: 0,
        containLabel: true,
      },
      legend: {
        data: legends,
        formatter: (name: string) => {
          return formatMessage({ id: name || NO_DATE.EMPTY });
        },
      },
      xAxis: {
        boundaryGap: ['20%', 0],
        data: xAxis,
        axisLabel: {
          showMaxLabel: true,
          formatter: (value: string) =>
            formatTimeXAxisText(value, filterParams?.date_type),
        },
      },
      yAxis: [
        {
          type: 'value',
          axisLabel: {
            formatter: (value: any) => {
              return echartsContent(value);
            },
          },
        },
        {
          type: 'value',
          axisLabel: {
            formatter: (value: any) => {
              return value * 100 + '%';
            },
          },
        },
      ],
      series: series,
    };
    setIntervalValue(currentOptions.series[0]?.data, currentOptions.yAxis[0]);
    setMinIntervalValue(
      currentOptions.series[1]?.data,
      currentOptions.yAxis[1],
    );

    return currentOptions;
  }, [chartMap, formatMessage, filterParams]);

  return (
    <EChartCommon
      options={options}
      height={260}
      empty={chartNoDataMap}
      optionsProps={{ type: 'line' }}
      leftTitle={formatMessage({ id: 'Top Critic Average' })}
      rightTitle={formatMessage({ id: 'Critics Recommend%' })}
    ></EChartCommon>
  );
}
